import { useState, useEffect } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/funksjoner", label: "Funksjoner" },
  { to: "/nokkeltall", label: "Nøkkeltall" },
  { to: "/sikkerhet", label: "Sikkerhet" },
  { to: "/for-hvem", label: "For hvem" },
  { to: "/om", label: "Om oss" },
  { to: "/priser", label: "Priser" },
];

function Wordmark({ subtitle = "Styrerom", invert = false }) {
  return (
    <span className="flex flex-col leading-none">
      <img src="/murphy-logo.png" alt="murphy" className={`h-[20px] w-auto object-contain ${invert ? "invert" : "brightness-0"}`} />
      <span className={`mt-1 text-[9px] font-medium uppercase tracking-[0.28em] ${invert ? "text-neutral-500" : "text-neutral-400"}`}>{subtitle}</span>
    </span>
  );
}

function FooterCol({ title, links }) {
  return (
    <div>
      <p className="text-sm font-semibold text-white">{title}</p>
      <ul className="mt-4 space-y-2 text-sm">
        {links.map(([label, href]) => (
          <li key={label}>
            {href.startsWith("http") ? (
              <a href={href} target="_blank" rel="noreferrer" className="text-neutral-400 transition-colors hover:text-white">{label}</a>
            ) : (
              <Link to={href} className="text-neutral-400 transition-colors hover:text-white">{label}</Link>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function MarketingLayout() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    setOpen(false);
    window.scrollTo(0, 0);
  }, [pathname]);

  return (
    <div className="min-h-screen bg-white text-neutral-900 antialiased">
      <header className="sticky top-0 z-50 border-b border-neutral-100 bg-white/85 backdrop-blur">
        <div className="mx-auto flex h-16 w-full max-w-6xl items-center justify-between px-6 lg:px-8">
          <Link to="/" data-testid="marketing-logo"><Wordmark /></Link>

          <nav className="hidden items-center gap-6 md:flex">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.to.slice(1)}`}
                className={({ isActive }) => `text-sm font-medium transition-colors hover:text-neutral-900 ${isActive ? "text-neutral-900" : "text-neutral-500"}`}
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden items-center gap-3 md:flex">
            {user ? (
              <Link to="/hjem" data-testid="nav-til-styrerommet" className="inline-flex items-center gap-2 rounded-full bg-neutral-900 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-neutral-700">
                Gå til styrerommet <ArrowRight className="h-4 w-4" />
              </Link>
            ) : (
              <>
                <Link to="/login" data-testid="nav-logg-inn" className="text-sm font-semibold text-neutral-700 transition-colors hover:text-neutral-900">Logg inn</Link>
                <Link to="/register" data-testid="nav-kom-i-gang" className="inline-flex items-center rounded-full bg-neutral-900 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-neutral-700">Kom i gang</Link>
              </>
            )}
          </div>

          <button className="text-neutral-900 md:hidden" data-testid="nav-menu-toggle" onClick={() => setOpen((v) => !v)} aria-label="Meny">
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {open ? (
          <div className="border-t border-neutral-100 bg-white md:hidden" data-testid="nav-mobile">
            <div className="space-y-1 px-6 py-4">
              {NAV.map((n) => (
                <Link key={n.to} to={n.to} className="block py-2 text-sm font-medium text-neutral-700">{n.label}</Link>
              ))}
              <div className="pt-3">
                {user ? (
                  <Link to="/hjem" className="block rounded-full bg-neutral-900 px-5 py-2.5 text-center text-sm font-semibold text-white">Gå til styrerommet</Link>
                ) : (
                  <div className="flex flex-col gap-2">
                    <Link to="/login" className="block rounded-full border border-neutral-300 px-5 py-2.5 text-center text-sm font-semibold">Logg inn</Link>
                    <Link to="/register" className="block rounded-full bg-neutral-900 px-5 py-2.5 text-center text-sm font-semibold text-white">Kom i gang</Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : null}
      </header>

      <main>
        <Outlet />
      </main>

      <footer className="bg-neutral-950 text-neutral-300">
        <div className="mx-auto grid w-full max-w-6xl gap-10 px-6 py-16 md:grid-cols-4 lg:px-8">
          <div>
            <Wordmark invert />
            <p className="mt-4 max-w-[16rem] text-sm text-neutral-400">Fordi alt som kan gå galt, ikke trenger å gå galt.</p>
          </div>
          <FooterCol title="Produkt" links={[["Funksjoner", "/funksjoner"], ["Nøkkeltall", "/nokkeltall"], ["Priser", "/priser"]]} />
          <FooterCol title="Selskapet" links={[["Om oss", "/om"], ["Sikkerhet", "/sikkerhet"], ["For hvem", "/for-hvem"], ["Kontakt", "/kontakt"], ["murphy.no", "https://murphy.no"]]} />
          <div>
            <p className="text-sm font-semibold text-white">Kontakt</p>
            <div className="mt-4 space-y-1 text-sm text-neutral-400">
              <p className="text-neutral-200">Murphy AS</p>
              <p>Torvgata 25, 2000 Lillestrøm</p>
              <a href="mailto:post@murphy.no" className="block transition-colors hover:text-white">post@murphy.no</a>
              <a href="tel:+4740031163" className="block transition-colors hover:text-white">+47 400 31 163</a>
            </div>
          </div>
        </div>
        <div className="border-t border-neutral-800 py-6 text-center text-xs text-neutral-500">
          © {new Date().getFullYear()} Murphy AS. Alle rettigheter forbeholdt.
        </div>
      </footer>
    </div>
  );
}
